import { Form } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useDeleteEmployeeWorkScheduleForDateMutation,
  useLazyCheckEmployeeWorkScheduleQuery,
  useUpdateWorkScheduleMutation,
} from '@app/api/WorkSchedule';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { isValidTime } from '@app/shared/components/TimeInput';

import { useCalendarContext } from '@pages/Calendar/context';

import { isCompleteTimeRange } from '../utils';

const { useForm, useWatch } = Form;
type WorkScheduleActionMode = 'edit' | 'delete';

// Время в формате ЧЧ:ММ, вводится вручную (us/11381).
interface WorkScheduleActionFormValues {
  timeS: string;
  timeE: string;
}

interface UseEmployeeWorkScheduleActionModalParams {
  employeeId: number;
  employeeName: string;
  initialTimeS: string;
  initialTimeE: string;
  initialWarningText?: string;
  isOpen: boolean;
  mode: WorkScheduleActionMode;
  workScheduleId: number;
  onClose(): void;
}

export const useEmployeeWorkScheduleActionModal = ({
  employeeId,
  employeeName,
  initialTimeS,
  initialTimeE,
  initialWarningText,
  isOpen,
  mode,
  workScheduleId,
  onClose,
}: UseEmployeeWorkScheduleActionModalParams) => {
  const [form] = useForm<WorkScheduleActionFormValues>();
  const [warningText, setWarningText] = useState(initialWarningText ?? '');
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const {
    refreshCalendarData,
    selectedDateMoment,
    removeDisplayedEmployeeId,
    removeActiveWorkdayEmployeeId,
  } = useCalendarContext();
  const [updateWorkSchedule, { isLoading: isUpdateLoading }] =
    useUpdateWorkScheduleMutation();
  const [deleteWorkScheduleForDate, { isLoading: isDeleteLoading }] =
    useDeleteEmployeeWorkScheduleForDateMutation();
  const [checkEmployeeWorkSchedule] = useLazyCheckEmployeeWorkScheduleQuery();
  const initializedModalKeyRef = useRef<string | null>(null);
  const selectedDateString = useMemo(
    () => selectedDateMoment.format('YYYY-MM-DD'),
    [selectedDateMoment]
  );

  const selectedDateLabel = useMemo(
    () => selectedDateMoment.format('DD.MM.YYYY'),
    [selectedDateMoment]
  );

  const currentScheduleLabel = useMemo(() => {
    if (!isValidTime(initialTimeS) || !isValidTime(initialTimeE)) {
      return 'Не удалось определить расписание';
    }

    return `${initialTimeS} - ${initialTimeE}`;
  }, [initialTimeE, initialTimeS]);

  const watchedTimeS = useWatch('timeS', form);
  const watchedTimeE = useWatch('timeE', form);

  const normalizedInitialWarningText = initialWarningText ?? '';

  const modalInitialStateKey = useMemo(
    () =>
      JSON.stringify({
        mode,
        workScheduleId,
        employeeId,
        selectedDateString,
        initialTimeS,
        initialTimeE,
        initialWarningText: normalizedInitialWarningText,
      }),
    [
      mode,
      workScheduleId,
      employeeId,
      selectedDateString,
      initialTimeS,
      initialTimeE,
      normalizedInitialWarningText,
    ]
  );

  const buildIsoString = useCallback(
    (time?: string) =>
      isValidTime(time) ? `${selectedDateString}T${time}:00.000Z` : null,
    [selectedDateString]
  );

  const checkScheduleConflicts = useCallback(
    async (timeS?: string, timeE?: string) => {
      if (mode !== 'edit') {
        return;
      }

      if (!ActiveBranchId || timeS == null || timeE == null) {
        return;
      }

      const timeSValue = buildIsoString(timeS);
      const timeEValue = buildIsoString(timeE);

      if (!timeSValue || !timeEValue) {
        return;
      }

      try {
        const result = await checkEmployeeWorkSchedule({
          ActiveBranchId,
          employeeId,
          timeS: timeSValue,
          timeE: timeEValue,
        }).unwrap();

        setWarningText(result.WarningForEdit ?? '');
      } catch (error) {
        showNotification(error);
      }
    },
    [
      buildIsoString,
      checkEmployeeWorkSchedule,
      employeeId,
      mode,
      ActiveBranchId,
      showNotification,
    ]
  );

  const syncFormAndCheckScheduleConflicts = useCallback(() => {
    if (!isOpen) {
      initializedModalKeyRef.current = null;
      return;
    }

    const isNewModalSession =
      initializedModalKeyRef.current !== modalInitialStateKey;

    if (isNewModalSession) {
      initializedModalKeyRef.current = modalInitialStateKey;

      form.setFieldsValue({
        timeS: initialTimeS,
        timeE: initialTimeE,
      });

      setWarningText(normalizedInitialWarningText);
      return;
    }

    if (mode !== 'edit') {
      return;
    }

    // Пока время вводится или интервал некорректен, бэкенд не спрашиваем.
    if (!isCompleteTimeRange(watchedTimeS, watchedTimeE)) {
      return;
    }

    const isInitialInterval =
      watchedTimeS === initialTimeS && watchedTimeE === initialTimeE;

    if (isInitialInterval) {
      setWarningText(normalizedInitialWarningText);
      return;
    }

    void checkScheduleConflicts(watchedTimeS, watchedTimeE);
  }, [
    form,
    isOpen,
    mode,
    initialTimeS,
    initialTimeE,
    watchedTimeS,
    watchedTimeE,
    modalInitialStateKey,
    normalizedInitialWarningText,
    checkScheduleConflicts,
  ]);

  const handleClose = useCallback(() => {
    form.resetFields();
    setWarningText(initialWarningText ?? '');
    onClose();
  }, [form, initialWarningText, onClose]);

  const handleFinish = useCallback(
    async (values: WorkScheduleActionFormValues) => {
      if (mode !== 'edit' || !ActiveBranchId) return;

      const { timeS, timeE } = values;
      const timeSValue = buildIsoString(timeS);
      const timeEValue = buildIsoString(timeE);

      if (!timeSValue || !timeEValue) return;

      try {
        await updateWorkSchedule({
          ActiveBranchId,
          data: {
            Id: workScheduleId,
            UserId: employeeId,
            BranchId: ActiveBranchId,
            TimeS: timeSValue,
            TimeE: timeEValue,
            IsIgnoreRecords: true,
          },
        }).unwrap();

        showNotification('Расписание исполнителя успешно обновлено', 'success');
        refreshCalendarData();
        handleClose();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      buildIsoString,
      employeeId,
      handleClose,
      mode,
      ActiveBranchId,
      refreshCalendarData,
      showNotification,
      updateWorkSchedule,
      workScheduleId,
    ]
  );

  const handleDelete = useCallback(async () => {
    if (mode !== 'delete' || !ActiveBranchId) return;

    try {
      await deleteWorkScheduleForDate({
        ActiveBranchId,
        employeeId,
        date: selectedDateString,
      }).unwrap();

      showNotification('Рабочий день в расписании удален', 'success');
      removeDisplayedEmployeeId(String(employeeId));
      removeActiveWorkdayEmployeeId(String(employeeId));
      refreshCalendarData();
      handleClose();
    } catch (error) {
      showNotification(error);
    }
  }, [
    deleteWorkScheduleForDate,
    employeeId,
    handleClose,
    mode,
    ActiveBranchId,
    removeDisplayedEmployeeId,
    removeActiveWorkdayEmployeeId,
    refreshCalendarData,
    selectedDateString,
    showNotification,
  ]);

  const handleConfirm = useCallback(() => {
    if (mode === 'edit') {
      form.submit();
      return;
    }

    void handleDelete();
  }, [form, handleDelete, mode]);

  return {
    form,
    employeeName,
    warningText,
    currentScheduleLabel,
    selectedDateLabel,
    isLoading: isUpdateLoading || isDeleteLoading,
    handleClose,
    handleConfirm,
    handleFinish,
    syncFormAndCheckScheduleConflicts,
  };
};
